import { Avatar, Icons, OpennessMeter, SectionHeader, StateScreen } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { track } from "../../features/analytics/track.ts";
import { customerBackendApi } from "../../features/api/backend.ts";
import { useT } from "../../features/i18n/index.ts";
import { useSession } from "../../features/session/session-context.tsx";
import { getMode } from "../../features/tonight/mode.ts";
import { mockTables } from "../../mocks/data.ts";

export const Route = createFileRoute("/_tabs/around")({
  component: AroundScreen,
});

function AroundScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { mode } = useSession();
  const modeMeta = getMode(mode);
  const backendEnabled = customerBackendApi !== null;
  // Server-side consent filtering already excludes private/paused/blocked
  // parties; we only drop the caller's own table for the discovery list.
  const aroundQuery = useQuery({
    queryKey: ["customer", "around"],
    queryFn: () =>
      customerBackendApi?.listAroundTables() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
    refetchInterval: 10_000,
  });
  const visible = backendEnabled
    ? (aroundQuery.data ?? [])
        .filter((entry) => !entry.isOwnTable)
        .map((entry) => ({
          id: entry.tableId,
          code: entry.code,
          people: entry.partySize,
          statusTh: entry.acceptingGifts ? t("around.acceptingGifts") : t("around.openTonight"),
          mode: "soft_open" as const,
          emoji: "🍸",
          playing: false,
        }))
    : mockTables.filter((mock) => mock.mode !== "private");

  // AEV-013 around_viewed: opt-in discovery view, counts only (no per-table list).
  useEffect(() => {
    track("around_viewed", { visible_table_count: visible.length, viewer_mode: mode });
  }, [visible.length, mode]);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => navigate({ to: "/room" })}
        className="flex w-full items-center justify-between rounded-lg border border-hairline bg-glass px-4 py-3 text-left"
      >
        <span className="text-cream text-sm">{t("around.floorPlanRow")}</span>
        <span className="text-gold text-xs">{t("around.openMap")}</span>
      </button>
      {/* your own visibility — consent made visible */}
      <div className="glass relative overflow-hidden rounded-lg px-4 py-3.5">
        <div className="flex items-center gap-3">
          <span className="text-gold">
            {mode === "private" ? <Icons.IconLock size={18} /> : <Icons.IconEye size={18} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-cream text-sm">
              {mode === "private"
                ? t("around.youHidden")
                : t("around.youOpen", { mode: modeMeta.label })}
            </div>
            <div className="text-cream-3 text-xs">{t("around.visibilityNote")}</div>
          </div>
          <button
            type="button"
            onClick={() => navigate({ to: "/tonight-mode" })}
            className="shrink-0 font-medium text-gold text-xs"
          >
            {t("around.change")}
          </button>
        </div>
      </div>

      <SectionHeader
        kicker={t("around.kicker")}
        title={t("around.title")}
        right={
          <span className="text-cream-3 text-xs">
            {t("around.openCount", { count: visible.length })}
          </span>
        }
      />

      {visible.length === 0 ? (
        <div className="h-[40vh]">
          <StateScreen
            title={
              backendEnabled && aroundQuery.isLoading ? t("around.loading") : t("around.emptyTitle")
            }
            body={
              backendEnabled && aroundQuery.isError ? t("around.loadError") : t("around.emptyBody")
            }
          />
        </div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((table) => {
            const tm = getMode(table.mode);
            return (
              <button
                key={table.id}
                type="button"
                onClick={() => navigate({ to: "/tables/$tableId", params: { tableId: table.id } })}
                className="glass jod-fade-up flex w-full items-center gap-3.5 rounded-lg px-4 py-3.5 text-left"
              >
                <Avatar glyph={table.emoji} size={46} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold text-cream">
                      {t("common.table", { code: table.code })}
                    </span>
                    <span className="text-cream-4 text-xs">·</span>
                    <span className="flex items-center gap-1 text-cream-3 text-xs">
                      <Icons.IconPeople size={13} /> {table.people}
                    </span>
                    {table.playing ? (
                      <span className="rounded-full bg-[oklch(86%_0.18_120_/_14%)] px-2 py-0.5 text-[0.62rem] text-lime">
                        {t("around.playing")}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="truncate text-cream-3 text-xs">{table.statusTh}</span>
                    <OpennessMeter level={tm.openness} tone={tm.tone} />
                  </div>
                </div>
                <Icons.IconChevDown size={18} style={{ transform: "rotate(-90deg)" }} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
